import { h, render } from 'preact';
import htm from 'htm';
import { getCardComponent } from './cards/registry.js';
import { Tracking } from '../../utils/tracking.js';

const html = htm.bind(h);

/**
 * Formats time/date strings for timeline badges, abbreviating full month names.
 * @param {string} time
 * @returns {string}
 */
function formatTimeBadge(time) {
  if (!time) return '';
  return time
    .replace(/January/gi, 'Jan')
    .replace(/February/gi, 'Feb')
    .replace(/March/gi, 'Mar')
    .replace(/April/gi, 'Apr')
    .replace(/August/gi, 'Aug')
    .replace(/September/gi, 'Sep')
    .replace(/October/gi, 'Oct')
    .replace(/November/gi, 'Nov')
    .replace(/December/gi, 'Dec');
}

/**
 * Groups items by their time_suggestion.
 * @param {Array} items
 * @returns {Array} Array of groups { time, items }
 */
function groupItemsByTime(items) {
  if (!items) return [];
  const groups = [];
  let currentGroup = null;

  items.forEach(item => {
    const time = item.time_suggestion || '';
    if (!currentGroup || currentGroup.time !== time) {
      currentGroup = { time, items: [item] };
      groups.push(currentGroup);
    } else {
      currentGroup.items.push(item);
    }
  });
  return groups;
}

/**
 * BlocksFeed component maps over list of recommendation cards and renders them
 * as styled individual cards matching Figma design, with timeline support when time suggestions exist.
 * Each item is dispatched to its dedicated card component via the card registry.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.data - The blocks payload from the API response.
 * @param {Object} props.ctx - Context containing t() and config options.
 */
export function BlocksFeed({ data, ctx }) {
  const blocks = data?.blocks || [];

  return html`
    <div class="mta-blocks-container">
      ${blocks.map((block, bIdx) => {
    const weather = block.weather || {};
    const hasWeather = weather.condition != null || weather.temperature != null;
    const hasTimeData =
      Array.isArray(block.items) &&
      block.items.some((item) => item.time_suggestion != null && item.time_suggestion !== '');
    const showGroupHeader = (blocks.length > 1 || hasWeather) && (block.title || hasWeather);

    return html`
          <div class="mta-block-group" key=${bIdx}>
            ${showGroupHeader
        ? html`
                  <div class="mta-block-group-header">
                    ${block.title && html`<h4 class="mta-block-group-title">${block.title}</h4>`}
                    ${block.subtitle && html`<div class="mta-block-subtitle">${block.subtitle}</div>`}
                    ${hasWeather &&
          html`
                      <div class="mta-block-weather-subtext">
                        <span class="mta-weather-cond">${weather.condition || ''}</span>
                        ${weather.temperature != null && html`<span class="mta-weather-temp"> • ${weather.temperature}</span>`}
                      </div>
                    `}
                  </div>
                `
        : ''}

            ${hasTimeData
        ? html`
                  <div class="mta-timeline-list">
                    ${groupItemsByTime(block.items).map((group, gIdx) => {
          return html`
                        <div class="mta-timeline-row" key=${gIdx}>
                          <div class="mta-timeline-left">
                            <div
                              class=${`mta-timeline-time-badge ${!group.time ? 'mta-timeline-time-badge--bullet' : ''}`}
                            >
                              ${!group.time ? html`<span class="mta-timeline-bullet"></span>` : formatTimeBadge(group.time)}
                            </div>
                            <div class="mta-timeline-line"></div>
                          </div>
                          <div class="mta-timeline-right">
                            <div class="mta-cards-grid">
                              ${group.items.map((item, iIdx) => {
                const CardComponent = getCardComponent(item.type);
                return html`
                                  <${CardComponent} item=${item} block=${block} ctx=${ctx} key=${iIdx} />
                                `;
              })}
                            </div>
                          </div>
                        </div>
                      `;
        })}
                  </div>
                `
        : html`
                  <div class="mta-cards-grid">
                    ${block.items?.map(
          (item, iIdx) => {
            const CardComponent = getCardComponent(item.type);
            return html`
                        <${CardComponent} item=${item} block=${block} ctx=${ctx} key=${iIdx} />
                      `;
          }
        )}
                  </div>
                `}
          </div>
        `;
  })}
    </div>
  `;
}

/**
 * mountBlocksView instantiates the Preact rendering flow inside the provided container.
 *
 * @param {HTMLElement} container - Element target to inject the Preact root.
 * @param {Object} data - Payload data.
 * @param {Object} ctx - View context configuration.
 * @returns {{ destroy: Function, update: Function }}
 */
export function mountBlocksView(container, data, ctx) {
  const handleBlocksClick = (e) => {
    const link = e.target.closest('.mta-map-popup-link');
    if (link) {
      const clientName = ctx.config?.client || '';
      Tracking.trackResultClick(clientName, link.innerText || link.textContent || '', link.href);
    }
  };

  container.addEventListener('click', handleBlocksClick);

  render(html`<${BlocksFeed} data=${data} ctx=${ctx} />`, container);

  return {
    destroy() {
      container.removeEventListener('click', handleBlocksClick);
      render(null, container);
    },
    update(newData) {
      render(html`<${BlocksFeed} data=${newData} ctx=${ctx} />`, container);
    }
  };
}
